// infcode_change - new file
import fs from "fs/promises"
import path from "path"
import matter from "gray-matter"
import * as Log from "@opencode-ai/core/util/log"
import { discoverProjectDirectories } from "./discovery"
import { addMigrationItem, createMigrationReport, type MigrationReport } from "./types"
import { ensureTrailingNewline, errorMessage, isRecord, writeBackup } from "./util"

const LOG_PREFIX = "[infcode:migration:rules]"
const log = Log.create({ service: LOG_PREFIX })

type Scope = "global" | "project"
type Trigger = "always" | "manual" | "smart"

export type RulesMigrationItem = {
  status: "migrated" | "rewritten" | "skipped" | "failed"
  scope: Scope
  sourcePath: string
  targetPath?: string
  backupPath?: string
  reason?: string
  message?: string
}

export type RulesMigrationReport = MigrationReport<RulesMigrationItem>

type LegacyRuleDocument = {
  data: Record<string, unknown>
  content: string
}

export namespace RulesMigration {
  export type Item = RulesMigrationItem
  export type Report = RulesMigrationReport

  export async function run(input: { home: string }): Promise<RulesMigrationReport> {
    const report = createMigrationReport<RulesMigrationItem>()
    await migrateRulesDirectory({
      report,
      scope: "global",
      rulesDir: path.join(input.home, ".infcode", "rules"),
      backupDir: path.join(input.home, ".infcode", "migration", "v1-backup", "rules", "global"),
    })

    const { projects, issues } = await discoverProjectDirectories(input.home)
    for (const issue of issues) {
      if (issue.kind === "sessions-json-invalid") {
        addMigrationItem(report, {
          status: "failed",
          scope: "project",
          sourcePath: issue.sourcePath,
          reason: "sessions-json-invalid",
          message: issue.message,
        })
        continue
      }
      addMigrationItem(report, {
        status: "skipped",
        scope: "project",
        sourcePath: issue.sourcePath,
        targetPath: path.join(issue.workspaceDirectory ?? "", ".infcode", "rules"),
        reason: "workspace-missing",
      })
    }

    for (const projectDir of projects) {
      await migrateRulesDirectory({
        report,
        scope: "project",
        rulesDir: path.join(projectDir, ".infcode", "rules"),
        backupDir: path.join(projectDir, ".infcode", "migration", "v1-backup", "rules"),
      })
    }

    return report
  }
}

async function migrateRulesDirectory(input: {
  report: RulesMigrationReport
  scope: Scope
  rulesDir: string
  backupDir: string
}) {
  const files = await listFirstLevelLegacyRuleFiles(input.rulesDir)
  const mdBasenames = new Set(files.filter((file) => path.extname(file) === ".md").map((file) => path.basename(file, ".md")))
  const mdcBasenames = new Set(files.filter((file) => path.extname(file) === ".mdc").map((file) => path.basename(file, ".mdc")))

  for (const sourcePath of files) {
    const ext = path.extname(sourcePath)
    const basename = path.basename(sourcePath, ext)
    if (ext === ".mdc") {
      const targetPath = path.join(path.dirname(sourcePath), `${basename}.md`)
      if (mdBasenames.has(basename)) {
        addMigrationItem(input.report, {
          status: "skipped",
          scope: input.scope,
          sourcePath,
          targetPath,
          reason: "target-exists",
        })
        continue
      }
      await migrateRuleFile({
        report: input.report,
        scope: input.scope,
        sourcePath,
        targetPath,
        backupPath: path.join(input.backupDir, path.basename(sourcePath)),
        status: "migrated",
      })
      continue
    }

    if (mdcBasenames.has(basename)) continue

    await migrateRuleFile({
      report: input.report,
      scope: input.scope,
      sourcePath,
      targetPath: sourcePath,
      backupPath: path.join(input.backupDir, path.basename(sourcePath)),
      status: "rewritten",
    })
  }
}

async function migrateRuleFile(input: {
  report: RulesMigrationReport
  scope: Scope
  sourcePath: string
  targetPath: string
  backupPath: string
  status: "migrated" | "rewritten"
}) {
  try {
    const original = await fs.readFile(input.sourcePath, "utf8")
    await writeBackup(input.backupPath, original)
    const next = serializeRule(input.scope, parseLegacyRule(original))
    await fs.mkdir(path.dirname(input.targetPath), { recursive: true })
    await fs.writeFile(input.targetPath, next, "utf8")
    addMigrationItem(input.report, {
      status: input.status,
      scope: input.scope,
      sourcePath: input.sourcePath,
      targetPath: input.targetPath,
      backupPath: input.backupPath,
    })
  } catch (err) {
    const message = errorMessage(err)
    log.warn("failed to migrate v1 rule", {
      scope: input.scope,
      sourcePath: input.sourcePath,
      targetPath: input.targetPath,
      error: message,
    })
    addMigrationItem(input.report, {
      status: "failed",
      scope: input.scope,
      sourcePath: input.sourcePath,
      targetPath: input.targetPath,
      backupPath: input.backupPath,
      reason: "migration-failed",
      message,
    })
  }
}

async function listFirstLevelLegacyRuleFiles(root: string): Promise<string[]> {
  try {
    const entries = await fs.readdir(root, { withFileTypes: true })
    return entries
      .filter((entry) => entry.isFile() && (entry.name.endsWith(".md") || entry.name.endsWith(".mdc")))
      .map((entry) => path.join(root, entry.name))
      .sort()
  } catch {
    return []
  }
}

function parseLegacyRule(text: string): LegacyRuleDocument {
  const source = text.trimStart()
  const lines = source.split(/\r?\n/)
  if (lines[0]?.trim() !== "---") return { data: {}, content: text }
  const close = lines.findIndex((line, index) => index > 0 && line.trim() === "---")
  if (close < 0) return { data: {}, content: text }
  const fallbackContent = lines.slice(close + 1).join("\n")
  try {
    const parsed = matter(source) as { data: unknown; content: string }
    return {
      data: isRecord(parsed.data) ? parsed.data : {},
      content: parsed.content,
    }
  } catch {
    return { data: {}, content: fallbackContent }
  }
}

function serializeRule(scope: Scope, doc: LegacyRuleDocument): string {
  const content = ensureTrailingNewline(doc.content.trim())
  if (scope === "global") return content
  const meta: Record<string, unknown> = {}
  const description = typeof doc.data.description === "string" ? doc.data.description.trim() : ""
  const trigger = mapTrigger(doc.data)
  if (description) meta.description = description
  if (trigger) meta.trigger = trigger
  if (Object.keys(meta).length === 0) return content
  return matter.stringify(content, meta)
}

function mapTrigger(data: Record<string, unknown>): Trigger | undefined {
  const invokeMode = data.invokeMode
  if (invokeMode === "always" || invokeMode === "manual" || invokeMode === "smart") return invokeMode
  const trigger = data.trigger
  if (trigger === "always" || trigger === "manual" || trigger === "smart") return trigger
  return data.alwaysApply === true ? "always" : undefined
}
